import { useTracking } from '@act/react-components/tracking';
import { SuggestedNeedDto, NeedDto } from '@act/shared/data-transfer-objects';
import {
  Alert,
  Button,
  FlexContainer,
  FormBuilder,
  IconLibrary,
} from '@act/storybook';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import ZCodeContent from '@act/react-components/dialog-forms/learn-more-zcode-content';
import { GoalsSidePanel } from './GoalsSidePanel';
import { LearnMoreContent } from './LearnMoreContent';
import { useAddNeedForm } from './hooks/use-add-need-form';
import { useMemo } from 'react';

interface AddNeedContentProps {
  open: boolean;
  onClose: () => void;
  need?: NeedDto;
  suggestedNeed?: SuggestedNeedDto;
}

export const AddNeedContent = ({
  open,
  onClose,
  need,
  suggestedNeed,
}: AddNeedContentProps) => {
  const { generateTrackingProps } = useTracking();
  const {
    control,
    formState,
    handleSubmit,
    isSubmitting,
    handleSubmission,
    handleClose,
    selectedCategory,
    selectedIssue,
    selectedIssueZCode,
    openLearnMoreContent,
    setOpenLearnMoreContent,
    getFormConfig,
    needsDescriptionFF,
  } = useAddNeedForm({
    need,
    suggestedNeed,
    onClose,
    trackOpenState: true,
  });

  const alertContent = useMemo(() => {
    if (!selectedCategory) return null;
    return (
      <Box>
        <Alert
          severity="info"
          variant="tonal"
          title={selectedCategory.label}
          trackingName="add-need-alert"
          action={{
            label: 'Learn more',
            onClick: () => setOpenLearnMoreContent(true),
          }}
        >
          {`This issue has a target time window of ≤ ${selectedIssue?.defaultTimeFrameInDays} days.`}
        </Alert>
        <Divider sx={{ margin: '16px 0' }} />
      </Box>
    );
  }, [selectedCategory, selectedIssue?.defaultTimeFrameInDays, setOpenLearnMoreContent]);

  const textContent = useMemo(() => (
    <Typography
      variant="titleMedium"
      sx={{ fontWeight: 500, fontSize: '16px' }}
    >
      {needsDescriptionFF
        ? "What do you understand about this member's situation? (required)"
        : 'What is the reason for pursuing this need? (required)'}
    </Typography>
  ), [needsDescriptionFF]);

  const inputAdornment = useMemo(() => (
    <IconLibrary iconName="MagnifyingGlass" size={18} weight="regular" />
  ), []);

  const formConfig = useMemo(
    () => getFormConfig(alertContent, textContent, inputAdornment),
    [getFormConfig, alertContent, textContent, inputAdornment]
  );

  return (
    <>
      {openLearnMoreContent ? (
        <LearnMoreContent
          open={openLearnMoreContent}
          setOpen={setOpenLearnMoreContent}
          selectedIssueZCode={<ZCodeContent zcode={selectedIssueZCode} />}
          selectedCategory={selectedCategory}
        />
      ) : (
        <GoalsSidePanel
          open={open}
          animated={false}
          titleContent={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: '16px',
              }}
            >
              <IconButton onClick={handleClose}>
                <IconLibrary iconName="ArrowLeft" size={32} weight="regular" />
              </IconButton>
              <Typography sx={{ alignSelf: 'center' }} variant="titleLarge">
                {need ? 'Edit Need' : 'Add New Need'}
              </Typography>
            </Box>
          }
          content={
            <FlexContainer direction="column" gap={2}>
              <FormBuilder
                config={formConfig}
                control={control}
                formState={formState}
                disabled={isSubmitting}
              />
            </FlexContainer>
          }
          footerContent={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                {...generateTrackingProps(
                  need
                    ? 'update-need-from-call-submit-button'
                    : 'add-new-need-from-call-submit-button'
                )}
                onClick={handleSubmit(handleSubmission)}
              >
                {isSubmitting
                  ? need
                    ? 'Updating...'
                    : 'Creating...'
                  : 'Submit'}
              </Button>
            </Box>
          }
        />
      )}
    </>
  );
};
